import { useSyncExternalStore } from 'react';

/**
 * App-level notice stack (testimony plan rev 4 section 32.2). The three top
 * notices keep their own state, owners and runtimes; each only portals its
 * output into one stable slot of `AppNoticeStack`, a single fixed column below
 * the Settings / Bible corner links, so they can never cover each other.
 * Presentation only: no notice state lives here.
 *
 * Geometry is published in viewport pixels: `top` is the stack's top edge and
 * `naturalHeight` its uncapped content height (0 when nothing is shown). Home
 * converts both into its own container coordinates and may request a height
 * cap; the cap never changes `naturalHeight`, so it cannot feed back into the
 * value it was derived from. `displayedHeight` is the band the stack actually
 * occupies (the cap respected; equal to `naturalHeight` when uncapped). Full
 * screen surfaces reserve space below their header from the displayed band
 * (`useNoticeStackReserve`), and sheets, dialogs and sticky headers keep below
 * its clearance (`noticeStackClearance`, `useNoticeStackClearance`); nothing
 * reads it for the cap. While a sheet's or dialog's keyboard is open the stack
 * may also take a keyboard room cap (`setNoticeStackRoomRequest`), which it
 * computes itself from its fixed top and uncapped height.
 */
export type NoticeSlot = 'warning' | 'draft' | 'kept';

export interface NoticeStackGeometry {
  top: number;
  naturalHeight: number;
  displayedHeight: number;
}

const slots: Record<NoticeSlot, HTMLElement | null> = { warning: null, draft: null, kept: null };
let geometry: NoticeStackGeometry | null = null;
let cap: number | null = null;
const slotListeners = new Set<() => void>();
const geometryListeners = new Set<() => void>();
const capListeners = new Set<() => void>();

function notify(listeners: Set<() => void>): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      // Observers only.
    }
  }
}

function subscribe(listeners: Set<() => void>, listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setNoticeSlot(slot: NoticeSlot, element: HTMLElement | null): void {
  if (slots[slot] === element) return;
  slots[slot] = element;
  notify(slotListeners);
}

/** The slot a notice portals into; null until `AppNoticeStack` has mounted it. */
export function useNoticeSlot(slot: NoticeSlot): HTMLElement | null {
  return useSyncExternalStore(
    (listener) => subscribe(slotListeners, listener),
    () => slots[slot],
    () => null,
  );
}

export function publishNoticeStackGeometry(next: NoticeStackGeometry | null): void {
  if (
    next === geometry ||
    (next !== null && geometry !== null &&
      next.top === geometry.top && next.naturalHeight === geometry.naturalHeight &&
      next.displayedHeight === geometry.displayedHeight)
  ) return;
  geometry = next;
  notify(geometryListeners);
}

export function getNoticeStackGeometry(): NoticeStackGeometry | null {
  return geometry;
}

/**
 * The first viewport row clear of the notices, in viewport pixels, rounded
 * up: the bottom of the displayed band, which for an uncapped stack already
 * ends with the last notice's 8 px bottom margin; a capped stack has no
 * trailing margin, so 8 px is added. 0 while no notice shows. Every surface
 * that keeps its controls below the notices uses this one edge: the in-flow
 * reserve below a header, the sheet and dialog insets and the Bible chapter's
 * sticky header.
 */
export function noticeStackClearance(next: NoticeStackGeometry | null): number {
  if (!next || next.displayedHeight <= 0) return 0;
  const capped = next.displayedHeight < next.naturalHeight - 0.5;
  return Math.ceil(next.top + next.displayedHeight + (capped ? 8 : 0));
}

export function subscribeNoticeStackGeometry(listener: () => void): () => void {
  return subscribe(geometryListeners, listener);
}

/** Height cap in px requested by home when space is short; null removes it. */
export function setNoticeStackCap(next: number | null): void {
  if (cap === next) return;
  cap = next;
  notify(capListeners);
}

export function getNoticeStackCap(): number | null {
  return cap;
}

export function subscribeNoticeStackCap(listener: () => void): () => void {
  return subscribe(capListeners, listener);
}

/** The stack's smallest capped height: it then scrolls inside itself (Home's cap uses the same floor). */
export const NOTICE_STACK_MIN_HEIGHT = 48;

/**
 * Keyboard room requests (v0.2.0 follow-through: a small phone, Extra Large
 * text, notices and the soft keyboard). A sheet or dialog whose text field has
 * the keyboard open while a notice shows registers the room, in px, that its
 * field and its action row need between the stack's clearance and the
 * keyboard's top edge (`useNoticeStackKeyboardRoom`). `AppNoticeStack` honours
 * the largest request on every route (`noticeStackRoomCap`), combined with
 * Home's cap as the smaller of the two. A request is withdrawn when the
 * keyboard closes or the surface unmounts, so the stack returns to its size.
 * Only the stack's height changes: no notice is unmounted, hidden or moved.
 */
const roomRequests = new Map<object, number>();
const roomListeners = new Set<() => void>();

export function setNoticeStackRoomRequest(owner: object, room: number | null): void {
  const current = roomRequests.get(owner);
  if (room === null) {
    if (current === undefined) return;
    roomRequests.delete(owner);
  } else {
    if (current === room) return;
    roomRequests.set(owner, room);
  }
  notify(roomListeners);
}

/** The largest registered room request in px, or null when none is registered. */
export function getNoticeStackRoomRequest(): number | null {
  let largest: number | null = null;
  for (const room of roomRequests.values()) if (largest === null || room > largest) largest = room;
  return largest;
}

export function subscribeNoticeStackRoomRequest(listener: () => void): () => void {
  return subscribe(roomListeners, listener);
}

/**
 * The height cap a room request needs, in px, or null when the uncapped stack
 * already leaves the room. `visibleBottom` is the visual viewport's bottom edge
 * (the keyboard's top edge while it is open). The capped clearance
 * (`top + cap + 8`, see `noticeStackClearance`) is then at most
 * `visibleBottom - room`, unless the floor applies: the stack never goes below
 * `NOTICE_STACK_MIN_HEIGHT`. Derived from the uncapped height and the stack's
 * fixed top only, so it cannot feed back on itself.
 */
export function noticeStackRoomCap(
  measured: { top: number; naturalHeight: number },
  visibleBottom: number,
  room: number | null,
): number | null {
  if (room === null || measured.naturalHeight <= 0) return null;
  const limit = visibleBottom - room;
  if (measured.top + measured.naturalHeight <= limit) return null;
  return Math.max(NOTICE_STACK_MIN_HEIGHT, Math.floor(limit - measured.top - 8));
}
